import { useMemo } from 'react';
import ApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import moment from 'moment';

import type {
  HarborOperatorDashboardStatusItem,
  HarborOperatorDashboardTopHarbor,
  HarborOperatorDashboardTrendPoint,
} from '@/types/harbor-operator-dashboard.types';
import { formatFinnishCurrency, formatFinnishNumber } from '@/utils';
import { normalizeTrendPoints } from '@/utils/harborOperatorDashboardTrend';

export { normalizeTrendPoints } from '@/utils/harborOperatorDashboardTrend';

const formatPeriodLabel = (period: string) => {
  const parsed = moment(period, ['YYYY-MM', moment.ISO_8601], true);
  return parsed.isValid() ? parsed.format('MMM YY') : period;
};

const chartToolbar = { show: false };

/** ApexCharts bar/area paths read `globals.stroke.colors`; never pass `stroke: undefined`. */
const chartStrokeColors = ['var(--tw-primary)'];
const chartDonutColors = [
  'var(--tw-primary)',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#6366f1',
  '#94a3b8',
];

interface TrendChartProps {
  title: string;
  subtitle: string;
  data: HarborOperatorDashboardTrendPoint[];
  mode: 'bookings' | 'revenue';
  loading?: boolean;
}

export const HarborDashboardTrendChart = ({
  title,
  subtitle,
  data,
  mode,
  loading,
}: TrendChartProps) => {
  const trendPoints = useMemo(() => normalizeTrendPoints(data), [data]);

  const categories = useMemo(
    () => trendPoints.map((d) => formatPeriodLabel(d.period)),
    [trendPoints]
  );

  const series = useMemo(
    () =>
      mode === 'bookings'
        ? [{ name: 'Bookings', data: trendPoints.map((d) => d.bookings) }]
        : [{ name: 'Net revenue', data: trendPoints.map((d) => d.net_revenue) }],
    [mode, trendPoints]
  );

  const chartKey = useMemo(
    () => `${mode}-${categories.join('|')}-${series[0].data.join('|')}`,
    [mode, categories, series]
  );

  const options: ApexOptions = useMemo(() => {
    const revenueMax = Math.max(...series[0].data, 0);

    return {
      chart: {
        type: 'area',
        height: 280,
        toolbar: chartToolbar,
        fontFamily: 'inherit',
        animations: { enabled: true },
      },
      colors: chartStrokeColors,
      dataLabels: { enabled: false },
      xaxis: {
        categories,
        labels: { style: { colors: 'var(--tw-gray-500)', fontSize: '11px' } },
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        tickAmount: mode === 'revenue' && revenueMax === 0 ? 4 : undefined,
        labels: {
          style: { colors: 'var(--tw-gray-500)', fontSize: '11px' },
          formatter: (val) => {
            const n = Number(val);
            if (!Number.isFinite(n)) {
              return '0';
            }
            if (mode === 'revenue') {
              return formatFinnishNumber(n, { maximumFractionDigits: 0 });
            }
            return String(Math.round(n));
          },
        },
      },
      grid: { borderColor: 'var(--tw-gray-200)', strokeDashArray: 4 },
      tooltip: {
        y: {
          formatter: (val) =>
            mode === 'revenue'
              ? formatFinnishCurrency(val)
              : `${Math.round(Number(val))} bookings`,
        },
      },
      stroke: {
        curve: 'smooth',
        width: 2,
        colors: chartStrokeColors,
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 0.35,
          opacityFrom: mode === 'bookings' ? 0.45 : 0.35,
          opacityTo: 0.05,
        },
      },
    };
  }, [categories, mode, series]);

  return (
    <div className="card rounded-lg border border-gray-500 shadow-md p-4 h-full">
      <div className="mb-3">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <p className="text-xs text-gray-500">{subtitle}</p>
      </div>
      {loading ? (
        <p className="text-sm text-gray-500 py-16 text-center">Loading chart...</p>
      ) : trendPoints.length === 0 ? (
        <p className="text-sm text-gray-500 py-16 text-center">
          No trend data for this period yet.
        </p>
      ) : (
        <ApexChart
          id={`harbor-dashboard-trend-${mode}`}
          key={chartKey}
          options={options}
          series={series}
          type="area"
          width="100%"
          height={280}
        />
      )}
    </div>
  );
};

interface StatusDonutProps {
  items: HarborOperatorDashboardStatusItem[];
  loading?: boolean;
}

export const HarborDashboardStatusDonut = ({
  items,
  loading,
}: StatusDonutProps) => {
  const labels = items.map((i) => i.label);
  const series = items.map((i) => i.count);
  const totalBookings = series.reduce((sum, count) => sum + count, 0);

  const options: ApexOptions = useMemo(
    () => ({
      chart: { type: 'donut', height: 420, toolbar: chartToolbar },
      labels,
      legend: { show: false },
      dataLabels: { enabled: true },
      colors: chartDonutColors,
      stroke: { show: false },
      tooltip: {
        y: { formatter: (val) => `${Number(val)} bookings` },
      },
      plotOptions: { pie: { donut: { size: '68%' } } },
    }),
    [labels]
  );

  return (
    <div className="card rounded-lg border border-gray-500 shadow-md h-full">
      <div className="card-body p-5">
        <h2 className="text-base font-semibold mb-1 contrast:text-white/90">
          Bookings by status
        </h2>
        <p className="text-xs text-gray-500 mb-4">
          Distribution across your harbors
        </p>
        {loading ? (
          <p className="text-sm text-gray-500 py-16 text-center">
            Loading chart...
          </p>
        ) : totalBookings === 0 ? (
          <p className="text-sm text-gray-500 py-16 text-center">
            No bookings yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(260px,0.9fr)] lg:items-center">
            <div className="min-w-0">
              <div className="mx-auto w-full max-w-[620px]">
                <ApexChart
                  id="harbor_booking_status_donut"
                  options={options}
                  series={series}
                  type="donut"
                  width="100%"
                  height="420"
                />
              </div>
            </div>
            <div className="space-y-3 lg:pr-2">
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Total bookings
                </p>
                <p className="mt-1 text-2xl font-semibold text-gray-900">
                  {totalBookings}
                </p>
              </div>
              <div className="space-y-2">
                {items.map((item, index) => {
                  const count = item.count;
                  const percentage =
                    totalBookings > 0 ? (count / totalBookings) * 100 : 0;

                  return (
                    <div
                      key={`${item.label}-${index}`}
                      className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span
                          className="h-3 w-3 rounded-full shrink-0"
                          style={{
                            backgroundColor:
                              chartDonutColors[index % chartDonutColors.length],
                          }}
                        />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-gray-900">
                            {item.label}
                          </p>
                          <p className="text-xs text-gray-500">
                            {percentage.toFixed(1)}% of bookings
                          </p>
                        </div>
                      </div>
                      <p className="shrink-0 text-sm font-semibold text-gray-900">
                        {count}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface TopHarborsChartProps {
  items: HarborOperatorDashboardTopHarbor[];
  loading?: boolean;
}

export const HarborDashboardTopHarborsChart = ({
  items,
  loading,
}: TopHarborsChartProps) => {
  const categories = items.map((h) => h.harbor_name);
  const series = [{ name: 'Bookings', data: items.map((h) => h.bookings) }];

  const options: ApexOptions = useMemo(
    () => ({
      chart: { type: 'bar', height: 280, toolbar: chartToolbar },
      plotOptions: {
        bar: { horizontal: true, borderRadius: 4, barHeight: '65%' },
      },
      stroke: {
        show: true,
        width: 0,
        colors: chartStrokeColors,
      },
      colors: chartStrokeColors,
      dataLabels: { enabled: false },
      xaxis: {
        categories,
        labels: { style: { colors: 'var(--tw-gray-500)', fontSize: '11px' } },
      },
      yaxis: {
        labels: {
          style: { colors: 'var(--tw-gray-500)', fontSize: '11px' },
          maxWidth: 140,
        },
      },
      grid: { borderColor: 'var(--tw-gray-200)', strokeDashArray: 4 },
    }),
    [categories]
  );

  return (
    <div className="card rounded-lg border border-gray-500 shadow-md p-4 h-full">
      <div className="mb-3">
        <h3 className="text-base font-semibold text-gray-900">Top harbors</h3>
        <p className="text-xs text-gray-500">
          By confirmed and completed bookings (last 6 months)
        </p>
      </div>
      {loading ? (
        <p className="text-sm text-gray-500 py-16 text-center">
          Loading chart...
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500 py-16 text-center">
          No harbor activity in this period yet.
        </p>
      ) : (
        <ApexChart options={options} series={series} type="bar" height={280} />
      )}
    </div>
  );
};
